import React, { useState, useEffect } from 'react';
import { ATELIER, FONTS } from '../tokens';
import { ProjectsSidebar } from './ProjectsSidebar';
import { ThreadView } from './ThreadView';

/**
 * Atelier - the white workspace shell. Two columns:
 *   • ProjectsSidebar (left, 288px)   - projects + mood tiles + new conv inside project
 *   • ThreadView      (center, fluid) - header + unified thread + smart composer
 *
 * Fixed viewport so internal scrollers manage their own overflow.
 * Enhanced with a Scale Ratio Wrapper for perfect smaller-screen scalability.
 */
export function Atelier() {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const handleResize = () => {
      const designWidth = 1920;  // Baseline widescreen design width
      const designHeight = 1350; // Baseline height required to comfortably render all Bento content + Passport strip + Composer
      
      const scaleX = window.innerWidth / designWidth;
      const scaleY = window.innerHeight / designHeight;
      
      // Use the minimum ratio to guarantee complete layout visibility on both axes (no scrollbars, no clipping)
      let calculatedScale = Math.min(scaleX, scaleY);
      
      // Cap scale at 1 to preserve crisp rendering on larger resolutions
      calculatedScale = Math.min(calculatedScale, 1);
      
      if (window.innerWidth <= 1024) {
        setScale(1); // Mobile/Tablet uses responsive CSS
      } else {
        // Never scale below 0.85 to maintain extremely crisp, readable text dimensions
        setScale(Math.max(0.85, calculatedScale));
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const scaledStyle = scale !== 1 ? {
    position: 'fixed',
    top: 0,
    left: 0,
    width: `${100 / scale}vw`,
    height: `${100 / scale}vh`,
    transform: `scale(${scale})`,
    transformOrigin: 'top left',
    overflow: 'hidden',
  } : {
    position: 'fixed',
    inset: 0,
    overflow: 'hidden',
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div
      className="dgw-atelier"
      style={{
        ...scaledStyle,
        display: 'flex',
        background: ATELIER.bg,
        color: ATELIER.ink,
        fontFamily: FONTS.sans,
        fontSize: 14,
        // Subtle violet light from upper-left, like a window in the room
        backgroundImage:
          'radial-gradient(ellipse 1100px 700px at -10% -10%, rgba(167,139,250,0.06) 0%, transparent 60%), ' +
          'radial-gradient(ellipse 800px 600px at 110% 100%, rgba(236,72,153,0.04) 0%, transparent 55%)',
      }}
    >
      {/* Mobile Drawer Overlay */}
      {sidebarOpen && (
        <div 
          className="dgw-mobile-overlay"
          onClick={() => setSidebarOpen(false)} 
        />
      )}
      
      <div className={`dgw-sidebar-wrapper ${sidebarOpen ? 'open' : ''}`}>
        <ProjectsSidebar />
      </div>
      
      <div className="dgw-main-wrapper" style={{ flex: 1, minWidth: 0, position: 'relative' }}>
        <ThreadView />
        {/* Mobile Hamburger Button */}
        <button 
          className="dgw-mobile-hamburger"
          onClick={() => setSidebarOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  );
}
